import type { LevelEntry } from "./types";

// Hand-authored boss phrases. Each is a self-contained sequence that
// starts with a `boss` marker on its first ShapeSegment and ends with
// `bossEnd: true` on its last ShapeSegment. The streamer fires
// onBossStart on attach of the marked segment and onBossCleared when
// the bossEnd segment recycles.
//
// Phrases are deliberately gate-bookended so they're safe to splice
// into either an EndlessGenerator stream or the tail of a hand-
// authored story level without breaking the "phrases end on a gate"
// invariant the streamer relies on.

export type BossId =
  | "sweep_gauntlet"
  | "pulse_storm"
  | "mirror_maze"
  | "final_boss"
  | "reverse_gate"
  | "apex_gate";

export interface BossPhrase {
  id: BossId;
  banner: string;
  entries: LevelEntry[];
}

// Three sweep segments back-to-back. The kill-bar moves on every snare
// across rotating shapes (oct -> sq -> tri) so the player has to read
// the sweep AND adapt to changing panel counts. Ends on a friendly
// triangle to give the player a brief breath before the next gate.
export const sweep_gauntlet: BossPhrase = {
  id: "sweep_gauntlet",
  banner: "BOSS: SWEEP GAUNTLET",
  entries: [
    {
      shape: "octagon",
      speed: 22,
      length: 56,
      gaps: [3, 6],
      trick: "sweep",
      boss: { id: "sweep_gauntlet", banner: "BOSS: SWEEP GAUNTLET" },
    },
    { shape: "gate", to: "square" },
    { shape: "square", speed: 22, length: 44, gaps: [1], trick: "sweep" },
    { shape: "gate", to: "triangle" },
    {
      shape: "triangle",
      speed: 22,
      length: 40,
      gaps: [1],
      trick: "sweep",
      bossEnd: true,
    },
    { shape: "gate", to: "octagon" },
  ],
};

// Pulse-trick octagons with tight gap layouts. The walls black out on
// every kick, forcing the player to memorise gaps in advance. Ends on
// a calm gate to a fresh shape so the player can recover.
export const pulse_storm: BossPhrase = {
  id: "pulse_storm",
  banner: "BOSS: PULSE STORM",
  entries: [
    {
      shape: "octagon",
      speed: 20,
      length: 60,
      gaps: [2, 6],
      trick: "pulse",
      boss: { id: "pulse_storm", banner: "BOSS: PULSE STORM" },
    },
    {
      shape: "octagon",
      speed: 21,
      length: 52,
      gaps: [1, 4, 7],
      trick: "pulse",
    },
    {
      shape: "octagon",
      speed: 22,
      length: 48,
      gaps: [0, 3, 6],
      trick: "pulse",
      bossEnd: true,
    },
    { shape: "gate", to: "square" },
  ],
};

// Mirror segments alternating with gates so every gate transition the
// player has to mentally re-flip their controls. Includes a slow-mo
// orb in the middle as a "if you can grab it you survive" reward.
export const mirror_maze: BossPhrase = {
  id: "mirror_maze",
  banner: "BOSS: MIRROR MAZE",
  entries: [
    {
      shape: "square",
      speed: 22,
      length: 44,
      gaps: [1],
      trick: "mirror",
      boss: { id: "mirror_maze", banner: "BOSS: MIRROR MAZE" },
    },
    { shape: "gate", to: "octagon" },
    {
      shape: "octagon",
      speed: 22,
      length: 50,
      gaps: [3, 5],
      trick: "mirror",
      pickups: [{ type: "slowmo", panel: 3, z: 0.5 }],
    },
    { shape: "gate", to: "triangle" },
    {
      shape: "triangle",
      speed: 23,
      length: 40,
      gaps: [2],
      trick: "mirror",
      bossEnd: true,
    },
    { shape: "gate", to: "octagon" },
  ],
};

// Final boss: a combination phrase that throws all three tricks at the
// player in escalating order. Speeds top out at the upper end of the
// curve. A shield orb on the very first segment hands the player one
// "oh shit" insurance for the gauntlet to come.
export const final_boss: BossPhrase = {
  id: "final_boss",
  banner: "FINAL BOSS",
  entries: [
    {
      shape: "octagon",
      speed: 24,
      length: 50,
      gaps: [1, 5],
      trick: "pulse",
      pickups: [{ type: "shield", panel: 0, z: 0.4 }],
      boss: { id: "final_boss", banner: "FINAL BOSS" },
    },
    { shape: "gate", to: "square" },
    {
      shape: "square",
      speed: 25,
      length: 40,
      gaps: [2],
      trick: "mirror",
    },
    { shape: "gate", to: "triangle" },
    {
      shape: "triangle",
      speed: 26,
      length: 40,
      gaps: [0],
      trick: "sweep",
      bossEnd: true,
    },
    { shape: "gate", to: "octagon" },
  ],
};

// Reverse Gate boss: gravity flips for the duration of the phrase.
// ReverseGateFx hooks onBossStart with id "reverse_gate" and:
//   - rolls the camera 180° on Z (over ~0.6s)
//   - inverts world gravity (PhysicsWorld.setGravity(0, +G, 0))
//   - swaps the player's soft-Y pin sign (Player.setGravityFlipped)
//   - tells WorldRotator to report the OPPOSITE panel as the floor
//     so TileHighlight + score's floor-aware logic keep working
//   - forces the input-invert flag in MirrorTrickFx so left/right
//     "feel" like they still mean the same thing visually
//
// All four flips revert on bossEnd recycle. Five entries, all
// hexagons (so the player has plenty of safe panels for the unusual
// orientation), capped by a friendly closing gate back to octagon.
export const reverse_gate: BossPhrase = {
  id: "reverse_gate",
  banner: "BOSS: REVERSE GATE",
  entries: [
    {
      shape: "hexagon",
      speed: 22,
      length: 50,
      gaps: [3],
      boss: { id: "reverse_gate", banner: "BOSS: REVERSE GATE" },
    },
    {
      shape: "hexagon",
      speed: 23,
      length: 46,
      gaps: [1, 4],
    },
    {
      shape: "hexagon",
      speed: 23,
      length: 44,
      gaps: [2, 5],
      pickups: [{ type: "score", panel: 0, z: 0.55 }],
    },
    {
      shape: "hexagon",
      speed: 24,
      length: 40,
      gaps: [0, 3],
    },
    {
      shape: "hexagon",
      speed: 24,
      length: 38,
      gaps: [1, 4],
      bossEnd: true,
    },
    { shape: "gate", to: "octagon" },
  ],
};

// Apex Gate: the final-final boss for the story arc. Reuses the
// `reverse_gate` marker id on the engagement segment so the existing
// ReverseGateFx fires without modification, but layers sweep + mirror
// tricks across the closing two hexagon segments for a brutal finale.
//
// Layout intent:
//   - Seg 1 (HEX): a generous warmup with a single gap so the player
//     gets a steady look before the gravity flip.
//   - Seg 2 (HEX): carries the `reverse_gate` boss marker. World
//     gravity flips, camera rolls 180°, controls re-feel inverted.
//   - Seg 3 (HEX): a tighter post-flip rotation gauntlet under the
//     new orientation so the player can settle into the upside-down
//     reality.
//   - Seg 4 (HEX): SWEEP trick under the flipped orientation -- the
//     kill-bar dances across panels while the player is still
//     adjusting to the new floor. This is the spike.
//   - Seg 5 (HEX): MIRROR + bossEnd. Inputs feel doubly-inverted
//     (mirror + reverse-gate's input-invert), forcing a final
//     muscle-memory re-flip before the closing gate.
//
// banner stays "BOSS: APEX GATE" so the player reads it as the new
// boss; ReverseGateFx still picks up the reverse_gate marker.id and
// engages the camera/gravity/input flip pipeline.
export const apex_gate: BossPhrase = {
  id: "apex_gate",
  banner: "BOSS: APEX GATE",
  entries: [
    {
      shape: "hexagon",
      speed: 24,
      length: 52,
      gaps: [3],
      pickups: [{ type: "shield", panel: 0, z: 0.5 }],
    },
    {
      shape: "hexagon",
      speed: 25,
      length: 48,
      gaps: [1, 4],
      boss: { id: "reverse_gate", banner: "BOSS: APEX GATE" },
    },
    {
      shape: "hexagon",
      speed: 25,
      length: 44,
      gaps: [2, 5],
      pickups: [{ type: "score", panel: 0, z: 0.55 }],
    },
    {
      shape: "hexagon",
      speed: 26,
      length: 42,
      gaps: [0, 3],
      trick: "sweep",
    },
    {
      shape: "hexagon",
      speed: 26,
      length: 42,
      gaps: [1, 4],
      trick: "mirror",
      bossEnd: true,
    },
    { shape: "gate", to: "octagon" },
  ],
};

// Map + ordered list for round-robin lookup in EndlessGenerator. The
// phrases are returned as fresh shallow-cloned arrays of entries so
// successive runs through the same boss don't share entry references
// with the streamer's internal cursor (some entries get mutated with
// __crossed flags etc).
export const BOSS_PHRASES: BossPhrase[] = [
  sweep_gauntlet,
  pulse_storm,
  mirror_maze,
  reverse_gate,
  final_boss,
];

export function bossEntriesByIndex(index: number): LevelEntry[] {
  const phrase = BOSS_PHRASES[index % BOSS_PHRASES.length]!;
  return phrase.entries.map((e) => ({ ...e }));
}

export function bossEntriesById(id: BossId): LevelEntry[] {
  const phrase = BOSS_PHRASES.find((p) => p.id === id);
  if (!phrase) return [];
  return phrase.entries.map((e) => ({ ...e }));
}
